import React from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLocalStorage } from 'usehooks-ts';
import { jwtDecode } from 'jwt-decode';
import { DateTime } from 'luxon';

import Keycloak, { FlipOIDCUser } from '../lib/keycloak';
import Loading from './Loading';
import { useConfig } from './Config';
import ErrorHandler from './ErrorHandler';
import useBoolean from './Boolean';

interface FlipOIDCProps {
  children: React.ReactNode;
}

interface FlipMe {
  created: string;
  email: string;
  first_name: string;
  last_modified: string;
  last_name: string;
  loging_status: string;
  system_role: string;
  tenant: string;
  user_id: string;
  user_profile: {
    about_me_text?: string;
    department?: string;
    job_title?: string;
    location?: string;
    mobile_number?: string;
    phone_number?: string;
  };
  username: string;
}

interface FlipOIDCContextProps {
  authenticated: boolean;
  loading: boolean;
  user: FlipOIDCUser;
  token: string;
  me: FlipMe;
}

const FlipOIDCContext = React.createContext<FlipOIDCContextProps | undefined>(undefined);

function validToken(token) {
  if (!token) {
    return false;
  }

  try {
    // Check if there is a valid iat
    const { exp } = jwtDecode(token);
    if (!exp) return false;

    // Check to see if it has expired
    const when = DateTime.fromSeconds(exp);
    return when > DateTime.now();
  } catch (err) {
    return false;
  }
}

export function useFlipOIDC() {
  const context = React.useContext(FlipOIDCContext);
  if (!context) throw new Error('useFlipOIDC must be used inside FlipOIDC');
  return context;
}

export default function FlipOIDC({ children }: FlipOIDCProps) {
  const navigate = useNavigate();
  const { url, realm, clientId } = useConfig();

  const [error, setError] = React.useState(undefined);
  const [authenticated, setAuthenticated] = useBoolean(false);
  const [loading, setLoading] = useBoolean(false);
  const [token, setToken] = useLocalStorage(`flip.${realm}.token`, '');
  const [user, setUser] = useLocalStorage<FlipOIDCUser | undefined>(`flip.${realm}.user`, undefined);
  const [me, setMe] = useLocalStorage<FlipMe | undefined>(`flip.${realm}.me`, undefined);

  const load = React.useCallback(async () => {
    // If we already have a valid token, just move on
    if (validToken(token)) {
      setAuthenticated(true);
      setLoading(false);
      setUser(Keycloak.extractUser(token));
      setToken(token);
      return undefined;
    }

    setLoading(true);
    setError(undefined);

    try {
      const keycloak = new Keycloak({
        url,
        realm,
        clientId,
      });

      await keycloak.init();

      if (keycloak.authenticated) {
        setAuthenticated(keycloak.authenticated);
        setLoading(false);
        setUser(keycloak.user);
        setToken(keycloak.token);

        try {
          const { data } = await axios.get('/api/v2/me', {
            headers: {
              Authorization: `Bearer ${keycloak.token}`,
            },
          });

          setMe(data);
        } catch (err) {
          console.error('Error retreiving /me');
          // Do nothing
        }

        navigate({
          pathname: keycloak.state?.path,
          search: createSearchParams(keycloak.state.query).toString(),
        });
      } else {
        setAuthenticated(false);
        setLoading(false);
        setUser(undefined);
        setToken(undefined);
      }
    } catch (err) {
      setError(err.message);
    }

    return undefined;
  }, [
    token,
    setLoading,
    url,
    realm,
    clientId,
    setAuthenticated,
    setUser,
    setToken,
    navigate,
    setMe,
  ]);

  React.useEffect(() => {
    load();
  }, [load]);

  const value = React.useMemo(() => ({
    authenticated,
    loading,
    user,
    token,
    me,
  }), [
    authenticated,
    loading,
    user,
    token,
    me,
  ]);

  return (
    <FlipOIDCContext.Provider value={value}>
      <Loading loading={loading}>
        <ErrorHandler
          visible={!!error}
          message={error}
        >
          {authenticated && children}
        </ErrorHandler>
      </Loading>
    </FlipOIDCContext.Provider>
  );
}
